import { formatDistanceToNow } from 'date-fns';
import { Check, Circle, Clock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { StripeSyncStatus } from '@/services/types';

type BadgeVariant = 'success' | 'warning' | 'info' | 'secondary' | 'destructive';

/**
 * One entry per member of `StripeSyncStatus`, and the `Record` is what enforces
 * that: while the union was three members wide and the API had begun writing
 * `'not_applicable'` for the FREE row, `LABELS[status]` was `undefined` and the
 * pill rendered the six-character word.
 *
 * `pending` is the *default* pending label. A pending row that has Stripe ids
 * on file overrides it below — those two rows were indistinguishable before.
 */
const LABELS: Record<StripeSyncStatus, string> = {
  synced: 'Synced',
  pending: 'Not synced',
  failed: 'Failed',
  not_applicable: 'Not applicable',
};

const VARIANTS: Record<StripeSyncStatus, BadgeVariant> = {
  synced: 'success',
  pending: 'warning',
  failed: 'destructive',
  not_applicable: 'secondary',
};

/**
 * `pending` with Stripe ids on file. The word must not name the cause, because
 * three different histories produce the same row: a re-seed, a sync running
 * right now, and a sync whose Worker died between the pending write and either
 * terminal one. `Stale` is what is observable of all three.
 */
const STALE_NOTE =
  'Stripe ids are on file. The status has not been confirmed since — a re-seed, or a sync that did not finish.';

/** `pending` with no product id. Not "Never synced": a first sync in flight would make "never" false. */
const NOT_SYNCED_NOTE = 'No Stripe product or price is recorded for this plan.';

interface BadgeState {
  label: string;
  variant: BadgeVariant;
  Icon: LucideIcon | null;
  note: string | null;
}

function distance(lastSyncedAt: string): string {
  return formatDistanceToNow(new Date(lastSyncedAt), { addSuffix: true });
}

/**
 * M11's legend, plus the fifth status M11 never drew a row for.
 *
 * `Stale` is `info` rather than `warning` on the authority of M11's own script
 * (`mockup-parts/M11.html:161`, painted blue by `:49`); M11's caption at `:118`
 * says the opposite, and the rendered artifact is what a human approved.
 * `Not applicable` is `secondary` because one column must not carry one hue for
 * two meanings: `info` already says "the status is out of date, look at it",
 * and this row says "there is nothing here and never will be".
 */
function resolveBadgeState(
  status: StripeSyncStatus,
  lastSyncedAt: string | null,
  stripeProductId: string | null,
): BadgeState {
  if (status === 'synced') {
    return {
      label: lastSyncedAt
        ? `${LABELS.synced} ${distance(lastSyncedAt)}`
        : LABELS.synced,
      variant: VARIANTS.synced,
      Icon: Check,
      note: null,
    };
  }

  if (status === 'pending' && stripeProductId !== null) {
    return {
      label: lastSyncedAt ? `Stale · synced ${distance(lastSyncedAt)}` : 'Stale',
      variant: 'info',
      Icon: Clock,
      note: STALE_NOTE,
    };
  }

  if (status === 'pending') {
    return {
      label: LABELS.pending,
      variant: VARIANTS.pending,
      Icon: Circle,
      note: NOT_SYNCED_NOTE,
    };
  }

  return { label: LABELS[status], variant: VARIANTS[status], Icon: null, note: null };
}

interface StripeSyncBadgeProps {
  status: StripeSyncStatus;
  lastSyncedAt: string | null;
  /**
   * The third input the badge needs, and the only new one. It is what separates
   * a plan that has never had a Stripe object from one whose ids are on file
   * and whose status was merely reset by a re-seed. Both read `Pending` before.
   */
  stripeProductId: string | null;
}

export function StripeSyncBadge({
  status,
  lastSyncedAt,
  stripeProductId,
}: StripeSyncBadgeProps) {
  const { label, variant, Icon, note } = resolveBadgeState(
    status,
    lastSyncedAt,
    stripeProductId,
  );

  return (
    <>
      <Badge variant={variant} className="inline-flex items-center gap-1.5">
        {Icon ? <Icon aria-hidden="true" className="h-3 w-3 shrink-0" /> : null}
        {label}
      </Badge>
      {note ? (
        <span className="mt-1 block max-w-[30ch] text-xs text-muted-foreground">
          {note}
        </span>
      ) : null}
    </>
  );
}
